import { useState, useEffect } from "react";
import "./Shopping.css";

const PRODUCTS = [
  {
    id: 1, name: "Wireless Headphones", category: "Electronics",
    price: 2499, originalPrice: 3299, rating: 4.5, reviews: 1280,
    badge: "Best Seller",
    emoji: "🎧",
    description: "Premium sound with 40hr battery & active noise cancellation.",
  },
  {
    id: 2, name: "Smart Watch Pro", category: "Electronics",
    price: 8999, originalPrice: 11999, rating: 4.7, reviews: 875,
    badge: "New",
    emoji: "⌚",
    description: "Health tracking, GPS, AMOLED display, 7-day battery life.",
  },
  {
    id: 3, name: "Running Sneakers", category: "Footwear",
    price: 3499, originalPrice: 4999, rating: 4.3, reviews: 2100,
    badge: "Sale",
    emoji: "👟",
    description: "Ultra-lightweight foam sole for maximum comfort on every run.",
  },
  {
    id: 4, name: "Leather Backpack", category: "Bags",
    price: 1899, originalPrice: 2599, rating: 4.6, reviews: 640,
    badge: null,
    emoji: "🎒",
    description: "Genuine leather, fits 15\" laptop, water-resistant lining.",
  },
  {
    id: 5, name: "Coffee Maker Deluxe", category: "Kitchen",
    price: 5299, originalPrice: 6999, rating: 4.4, reviews: 980,
    badge: "Popular",
    emoji: "☕",
    description: "Brew barista-quality coffee at home in under 3 minutes.",
  },
  {
    id: 6, name: "Yoga Mat Premium", category: "Fitness",
    price: 899, originalPrice: 1299, rating: 4.2, reviews: 3200,
    badge: null,
    emoji: "🧘",
    description: "Non-slip, 6mm cushion, eco-friendly TPE material.",
  },
  {
    id: 7, name: "Portable Speaker", category: "Electronics",
    price: 1999, originalPrice: 2799, rating: 4.5, reviews: 1540,
    badge: "Hot Deal",
    emoji: "🔊",
    description: "360° surround sound, IP67 waterproof, 24hr playtime.",
  },
  {
    id: 8, name: "Sunglasses UV400", category: "Accessories",
    price: 1299, originalPrice: 1799, rating: 4.1, reviews: 720,
    badge: null,
    emoji: "🕶️",
    description: "Polarised lenses, lightweight titanium frame, UV400 shield.",
  },
];

const CATEGORIES = ["All", "Electronics", "Footwear", "Bags", "Kitchen", "Fitness", "Accessories"];

function Stars({ rating }) {
  return (
    <span className="stars">
      {[1, 2, 3, 4, 5].map((s) => (
        <span key={s} className={s <= Math.floor(rating) ? "star filled" : s - 0.5 <= rating ? "star half" : "star"}>★</span>
      ))}
    </span>
  );
}

function CartDrawer({ cart, products, onClose, onRemove, onQtyChange, onCheckout }) {
  const total = cart.reduce((sum, item) => {
    const p = products.find((p) => p.id === item.id);
    return sum + (p ? p.price * item.qty : 0);
  }, 0);

  return (
    <div className="cart-overlay" onClick={onClose}>
      <div className="cart-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="cart-header">
          <h2>Your Cart</h2>
          <button className="cart-close" onClick={onClose}>✕</button>
        </div>

        {cart.length === 0 ? (
          <div className="cart-empty">
            <span className="cart-empty-icon">🛒</span>
            <p>Your cart is empty</p>
          </div>
        ) : (
          <>
            <div className="cart-items">
              {cart.map((item) => {
                const p = products.find((p) => p.id === item.id);
                if (!p) return null;
                return (
                  <div key={item.id} className="cart-item">
                    <div className="cart-item-emoji">{p.emoji}</div>
                    <div className="cart-item-info">
                      <p className="cart-item-name">{p.name}</p>
                      <p className="cart-item-price">₹{p.price.toLocaleString()}</p>
                    </div>
                    <div className="cart-item-controls">
                      <button onClick={() => onQtyChange(item.id, item.qty - 1)}>−</button>
                      <span>{item.qty}</span>
                      <button onClick={() => onQtyChange(item.id, item.qty + 1)}>+</button>
                    </div>
                    <button className="cart-item-remove" onClick={() => onRemove(item.id)}>🗑</button>
                  </div>
                );
              })}
            </div>
            <div className="cart-footer">
              <div className="cart-total">
                <span>Total</span>
                <span className="cart-total-amount">₹{total.toLocaleString()}</span>
              </div>
              <button className="btn-checkout" onClick={onCheckout}>Proceed to Checkout →</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Toast({ message, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2200);
    return () => clearTimeout(t);
  }, [onDone]);
  return <div className="toast">{message}</div>;
}

export default function ShoppingPage() {
  const [cart, setCart] = useState([]);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [cartOpen, setCartOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [ordered, setOrdered] = useState(false);
  const [wishlist, setWishlist] = useState([]);

  const filtered = PRODUCTS.filter((p) => {
    const matchCat = category === "All" || p.category === category;
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.category.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const totalItems = cart.reduce((s, i) => s + i.qty, 0);

  function addToCart(id) {
    setCart((prev) => {
      const exists = prev.find((i) => i.id === id);
      if (exists) return prev.map((i) => i.id === id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { id, qty: 1 }];
    });
    const p = PRODUCTS.find((p) => p.id === id);
    setToast(`${p.name} added to cart!`);
  }

  function removeFromCart(id) {
    setCart((prev) => prev.filter((i) => i.id !== id));
  }

  function changeQty(id, qty) {
    if (qty < 1) { removeFromCart(id); return; }
    setCart((prev) => prev.map((i) => i.id === id ? { ...i, qty } : i));
  }

  function toggleWishlist(id) {
    setWishlist((prev) => prev.includes(id) ? prev.filter((w) => w !== id) : [...prev, id]);
  }

  function checkout() {
    setCart([]);
    setCartOpen(false);
    setOrdered(true);
    setTimeout(() => setOrdered(false), 3500);
  }

  return (
    <div className="shop-root">
      {/* NAVBAR */}
      <nav className="navbar">
        <div className="navbar-brand">
          <span className="brand-icon">◈</span>
          <span className="brand-name">SHOPIFY<span className="brand-accent">X</span></span>
        </div>
        <div className="navbar-search">
          <span className="search-icon">⌕</span>
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && <button className="search-clear" onClick={() => setSearch("")}>✕</button>}
        </div>
        <button className="cart-btn" onClick={() => setCartOpen(true)}>
          <span className="cart-icon">🛒</span>
          <span className="cart-label">Cart</span>
          {totalItems > 0 && <span className="cart-badge">{totalItems}</span>}
        </button>
      </nav>

      {/* HERO */}
      <section className="hero">
        <div className="hero-content">
          <p className="hero-eyebrow">New Season Arrivals</p>
          <h1 className="hero-title">Shop What<br /><span className="hero-accent">You Love</span></h1>
          <p className="hero-sub">Discover handpicked products at unbeatable prices.</p>
          <button className="btn-hero" onClick={() => document.getElementById("products").scrollIntoView({ behavior: "smooth" })}>
            Explore Now ↓
          </button>
        </div>
        <div className="hero-visual">
          <div className="hero-blob"></div>
          <div className="hero-float">🛍️</div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="categories-bar">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            className={`cat-pill ${category === cat ? "active" : ""}`}
            onClick={() => setCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </section>

      {/* PRODUCTS GRID */}
      <section className="products-section" id="products">
        <div className="section-header">
          <h2 className="section-title">
            {category === "All" ? "All Products" : category}
            <span className="product-count"> ({filtered.length})</span>
          </h2>
        </div>

        {filtered.length === 0 ? (
          <div className="empty-state">
            <span>😕</span>
            <p>No products found for "<strong>{search}</strong>"</p>
          </div>
        ) : (
          <div className="products-grid">
            {filtered.map((p, i) => {
              const inCart = cart.find((c) => c.id === p.id);
              const inWish = wishlist.includes(p.id);
              const discount = Math.round(((p.originalPrice - p.price) / p.originalPrice) * 100);
              return (
                <div className="product-card" key={p.id} style={{ animationDelay: `${i * 0.07}s` }}>
                  {p.badge && <span className="product-badge">{p.badge}</span>}
                  <button
                    className={`wishlist-btn ${inWish ? "wished" : ""}`}
                    onClick={() => toggleWishlist(p.id)}
                    title="Wishlist"
                  >
                    {inWish ? "♥" : "♡"}
                  </button>

                  <div className="product-emoji-wrap">
                    <span className="product-emoji">{p.emoji}</span>
                  </div>

                  <div className="product-info">
                    <span className="product-category">{p.category}</span>
                    <h3 className="product-name">{p.name}</h3>
                    <p className="product-desc">{p.description}</p>

                    <div className="product-rating">
                      <Stars rating={p.rating} />
                      <span className="rating-num">{p.rating}</span>
                      <span className="rating-reviews">({p.reviews.toLocaleString()})</span>
                    </div>

                    <div className="product-pricing">
                      <span className="product-price">₹{p.price.toLocaleString()}</span>
                      <span className="product-original">₹{p.originalPrice.toLocaleString()}</span>
                      <span className="product-discount">{discount}% off</span>
                    </div>
                  </div>

                  <button
                    className={`btn-add ${inCart ? "in-cart" : ""}`}
                    onClick={() => addToCart(p.id)}
                  >
                    {inCart ? `In Cart (${inCart.qty}) ✓` : "Add to Cart"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <div className="footer-brand">◈ SHOPIFYX</div>
        <p className="footer-text">© 2026 ShopifyX · Free shipping on orders above ₹999</p>
      </footer>

      {/* CART DRAWER */}
      {cartOpen && (
        <CartDrawer
          cart={cart}
          products={PRODUCTS}
          onClose={() => setCartOpen(false)}
          onRemove={removeFromCart}
          onQtyChange={changeQty}
          onCheckout={checkout}
        />
      )}

      {/* TOAST */}
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}

      {/* ORDER SUCCESS */}
      {ordered && (
        <div className="order-success">
          <span>🎉</span>
          <p>Order Placed Successfully!</p>
        </div>
      )}
    </div>
  );
}